/**
 * @fileoverview `useCopyToClipboard` — hook copie navigateur avec feedback.
 *
 * Encapsule `navigator.clipboard.writeText` avec un état `copied` transitoire
 * qui repasse à `false` après un délai — permet à l'UI d'afficher une icône
 * ou un toast "Copié !" éphémère sans gestion manuelle.
 *
 * Utilisé par `CopyButton` mais aussi appelable directement dans un composant
 * plus complexe (ex : `MerchantReference` qui affiche l'ULID + un statut
 * "Copié" contextuel).
 */

import { useCallback, useRef, useState } from 'react';

export interface UseCopyToClipboardOptions {
  /**
   * Délai (ms) avant que `copied` repasse à `false`. Défaut : 2000 ms —
   * assez pour que l'utilisateur voie le feedback, assez court pour ne
   * pas rester "collé" si on copie plusieurs valeurs rapidement.
   */
  timeout?: number;
}

export interface UseCopyToClipboardReturn {
  /**
   * Lance la copie de `value` dans le presse-papier. Retourne `true` si
   * la copie a réussi. Si la Clipboard API n'est pas disponible (contexte
   * non-secure, browser trop vieux), retourne `false` sans throw.
   */
  copy: (value: string) => Promise<boolean>;
  /**
   * `true` pendant les `timeout` ms qui suivent une copie réussie.
   * `false` en initial et après expiration.
   */
  copied: boolean;
}

/**
 * Hook standard copie-vers-presse-papier.
 *
 * @example
 * ```tsx
 * const { copy, copied } = useCopyToClipboard();
 * <button onClick={() => copy('#001540')}>{copied ? '✓' : 'Copier'}</button>
 * ```
 */
export function useCopyToClipboard(
  options: UseCopyToClipboardOptions = {},
): UseCopyToClipboardReturn {
  const { timeout = 2000 } = options;
  const [copied, setCopied] = useState(false);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const copy = useCallback(
    async (value: string): Promise<boolean> => {
      // Contextes sans HTTPS (localhost mis à part) et vieux navigateurs
      // n'ont pas de `navigator.clipboard`. On échoue silencieusement
      // plutôt que de crasher — l'appelant peut lire le retour.
      if (typeof navigator === 'undefined' || !navigator.clipboard) {
        return false;
      }
      try {
        await navigator.clipboard.writeText(value);
        setCopied(true);
        if (timerRef.current) clearTimeout(timerRef.current);
        timerRef.current = setTimeout(() => setCopied(false), timeout);
        return true;
      } catch {
        return false;
      }
    },
    [timeout],
  );

  return { copy, copied };
}
